import React, { useState, useEffect } from 'react';
import { rolesAPI, menusAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Shield, Copy } from 'lucide-react';
import { toast } from 'sonner';
import CreateRolDialog from '../components/CreateRolDialog';
import EditRolDialog from '../components/EditRolDialog';
import { usePermissions } from '../hooks/usePermissions';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRol, setSelectedRol] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const { isAdmin, canCreate, canView, canEdit, canDelete } = usePermissions();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!canView('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para ver roles');
      setLoading(false);
      return;
    }

    try {
      const [rolesRes, menusRes] = await Promise.all([
        rolesAPI.getAll(),
        menusAPI.getAll()
      ]);
      setRoles(rolesRes.data);
      setMenus(menusRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rol) => {
    if (!canEdit('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para editar roles');
      return;
    }
    setSelectedRol(rol);
    setShowEdit(true);
  };

  const handleDelete = async (rol) => {
    if (!canDelete('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para eliminar roles');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar el rol "${rol.nombre}"?`)) return;
    
    try {
      await rolesAPI.delete(rol.id);
      toast.success('Rol eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar rol');
    }
  };

  const getPermissionSummary = (permisos) => {
    if (!permisos || permisos.length === 0) return 'Sin permisos';
    
    // Count all menus
    const totalMenus = menus.length;
    const menusWithPermission = permisos.length;
    
    return `${menusWithPermission} de ${totalMenus} menús`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  const canCreateRol = canCreate('/roles') || isAdmin();
  const canEditRol = canEdit('/roles') || isAdmin();
  const canDeleteRol = canDelete('/roles') || isAdmin();
  const canViewRol = canView('/roles') || isAdmin();

  if (!canViewRol) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="bg-white border border-[#E8E8E7] shadow-sm p-6">
          <p className="text-[#888888]">No tiene permisos para ver esta página</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Roles
          </h1>
          <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1 hidden sm:block">Gestiona roles y permisos granulares</p>
        </div>
        {canCreateRol && (
          <Button
            onClick={() => setShowCreate(true)}
            size="sm"
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white self-start sm:self-auto"
            data-testid="create-rol-button"
          >
            <Plus size={16} className="mr-1 sm:mr-2" />
            Nuevo Rol
          </Button>
        )}
      </div>

      {/* Info Card - Hidden on mobile */}
      <Card className="bg-gradient-to-r from-[#9d8272]/10 to-[#8c926f]/10 border-l-4 border-[#9d8272] hidden sm:block">
        <CardContent className="p-4 lg:p-6">
          <div className="flex items-start gap-3">
            <Shield size={24} className="text-[#9d8272] mt-1 hidden lg:block" />
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mb-2 text-sm lg:text-base">Sistema de Permisos Granulares</h3>
              <p className="text-xs lg:text-sm text-[#5A5A5A] mb-2">
                Define permisos específicos para cada rol: <strong>Ver</strong>, <strong>Crear</strong>, <strong>Editar</strong> y <strong>Eliminar</strong> por cada opción del menú.
              </p>
              <p className="text-xs text-[#888888] hidden lg:block">
                Cada usuario tiene asignado un único rol que determina sus permisos en el sistema.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {roles.map((rol) => (
          <Card
            key={rol.id}
            className="bg-white border border-[#E8E8E7] shadow-sm hover:shadow-md transition-shadow"
            data-testid="rol-card"
          >
            <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={18} className="sm:w-5 sm:h-5 text-[#9d8272] flex-shrink-0" />
                    <CardTitle className="text-base sm:text-lg font-semibold text-[#2D2D2D] truncate">
                      {rol.nombre}
                    </CardTitle>
                  </div>
                  <p className="text-xs sm:text-sm text-[#888888] mb-2 sm:mb-3 line-clamp-2">{rol.descripcion || 'Sin descripción'}</p>
                  <Badge className="bg-[#c5b8a2] text-[#2D2D2D] border-0 text-[10px] sm:text-xs">
                    {getPermissionSummary(rol.permisos)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              <div className="flex gap-2">
                {canCreateRol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCloneSource(rol)}
                    className="border-[#c5b8a2] text-[#9d8272] hover:bg-[#9d8272]/10"
                    data-testid="clone-rol-button"
                    title="Clonar rol"
                  >
                    <Copy size={14} />
                  </Button>
                )}
                {canEditRol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(rol)}
                    className="flex-1 border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs sm:text-sm"
                    data-testid="edit-rol-button"
                  >
                    <Edit size={14} className="mr-1" />
                    Editar
                  </Button>
                )}
                {canDeleteRol && rol.nombre !== 'Administrador' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(rol)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="delete-rol-button"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {roles.length === 0 && (
        <Card className="bg-white border border-[#E8E8E7] shadow-sm">
          <CardContent className="text-center py-12">
            <Shield size={48} className="mx-auto mb-4 text-[#888888] opacity-50" />
            <p className="text-[#888888]">No hay roles configurados</p>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {(showCreate || cloneSource) && (
        <CreateRolDialog
          menus={menus}
          open={showCreate || !!cloneSource}
          initialData={cloneSource ? { nombre: cloneSource.nombre, descripcion: cloneSource.descripcion, permisos: cloneSource.permisos } : undefined}
          onClose={() => {
            setShowCreate(false);
            setCloneSource(null);
            loadData();
          }}
        />
      )}

      {showEdit && selectedRol && (
        <EditRolDialog
          rol={selectedRol}
          menus={menus}
          open={showEdit}
          onClose={() => {
            setShowEdit(false);
            setSelectedRol(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Roles;
